import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { authStore } from "../lib/authStore";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useI18n } from "../lib/i18n";
import { apiErrorMessage } from "../lib/apiErrors";
import { useDocumentTitle } from "../lib/useDocumentTitle";

/**
 * Where an activation link lands.
 *
 * Setting the password is the moment the account becomes real, so the API signs
 * the customer in on success — asking someone to type the password they just
 * chose, on the next screen, is a step with nothing behind it.
 *
 * The failure state used to offer to resend the link by email. The atelier has
 * no mail provider — `SMTP_HOST` is unset — so that offer could never be
 * honoured, and the page said "göndərildi" regardless. A guest account created
 * at checkout is keyed to the phone number the order was placed with, so the
 * way in without a link is the same SMS recovery the sign-in page uses.
 */
export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  useDocumentTitle("Hesabın aktivləşdirilməsi");
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!token) setError("");
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      setError(t("activate.too_short", "Parol ən azı 8 simvol olmalıdır."));
      return;
    }
    if (password !== confirm) {
      setError(t("activate.mismatch", "Parollar uyğun gəlmir."));
      return;
    }
    setError(""); setBusy(true);
    try {
      const res = await api.post("/auth/activate", { token, password });
      const { access_token, refresh_token } = res.data.tokens || {};
      authStore.setTokens(access_token, refresh_token);
      navigate("/account", { replace: true });
    } catch (requestError) {
      if (requestError?.response?.status === 400) setExpired(true);
      setError(apiErrorMessage(requestError, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--adf-cream)] text-[var(--adf-ink)]">
      <Header />
      <main id="main" className="adf-container flex justify-center py-14 md:py-20">
        <div className="grid w-full max-w-[440px] gap-6">
          <div className="grid gap-2 text-center">
            <span className="adf-small text-[var(--adf-espresso)]">Falcon Society</span>
            <h1 className="m-0 font-serif text-[34px] leading-tight">
              {t("activate.title", "Hesabınızı aktivləşdirin")}
            </h1>
            <p className="m-0 text-[14px] leading-6 text-[var(--adf-gray-2)]">
              {t("activate.intro", "Parol təyin edin — sifarişləriniz, Falcon Points və qarderobunuz bir hesabda toplanacaq.")}
            </p>
          </div>

          {!expired && token && (
            <form onSubmit={submit} className="grid gap-4" data-testid="activate-form">
              <label className="grid gap-2 adf-small text-[var(--adf-gray-3)]">
                {t("activate.password", "Yeni parol")}
                <input
                  type="password"
          placeholder="••••••••"
                  autoComplete="new-password"
                  data-testid="activate-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="adf-auth-line-input"
                />
              </label>
              <label className="grid gap-2 adf-small text-[var(--adf-gray-3)]">
                {t("activate.confirm", "Parolu təkrarlayın")}
                <input
                  type="password"
          placeholder="••••••••"
                  autoComplete="new-password"
                  data-testid="activate-confirm"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  className="adf-auth-line-input"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="activate-submit"
                className="h-[52px] bg-[var(--adf-ink)] adf-small text-[var(--adf-white-warm)] disabled:opacity-50"
              >
                {busy ? "…" : t("activate.submit", "Hesabı aktivləşdir")}
              </button>
            </form>
          )}

          {error && <div role="alert" className="text-[13px] leading-6 text-[#8a3b2e]" data-testid="activate-error">{error}</div>}

          {/* No link, or a spent one. There is no mail provider to resend
              through, so the way in is the phone the order was placed with. */}
          {(!token || expired) && (
            <div className="grid gap-3 border-t border-[var(--adf-gray-5)] pt-6" data-testid="activate-sms-route">
              <p className="m-0 text-[13px] leading-6 text-[var(--adf-gray-2)]">
                {t("activate.sms_help", "Linkiniz yoxdur və ya vaxtı bitib? Sifariş verdiyiniz telefon nömrəsi ilə parol təyin edin.")}
              </p>
              <Link
                to="/reset-password"
                data-testid="activate-sms-link"
                className="adf-cta adf-cta-dark min-h-[52px] w-full"
              >
                {t("activate.sms_cta", "Telefonla davam et")}
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
